import React, { useEffect, useMemo, useState } from 'react';
import { Grid, useMediaQuery, useTheme } from '@mui/material';
import { StepWrapper } from '../../../../../components/styled/StepWrapper';
import { ActionButtons } from '../../../ActionButtons/ActionButtons';
import { useDispatch, useSelector } from 'react-redux';
import {
  EServiceType,
  EUserType,
} from '../../../../../store/reducers/appointmentFrameReducer/types';
import {
  clearAppointmentSteps,
  selectCategories,
  selectService,
  selectSubService,
  setRecallsAreShown,
  setSelectedRecalls,
  setVehicle,
  setVehicleDataFromValueService,
  updateVehicle,
} from '../../../../../store/reducers/appointmentFrameReducer/actions';
import { RootState } from '../../../../../store/rootReducer';
import { EServiceCategoryPage, EServiceCenterName, ILoadedVehicle } from '../../../../../api/types';
import { EServiceCategoryType } from '../../../../../store/reducers/categories/types';
import { useTranslation } from 'react-i18next';
import { IEngineType } from '../../../../../store/reducers/vehicleDetails/types';
import RecallsByVinModal from '../../../RecallsByVinModal/RecallsByVinModal';
import NoRecallsModal from '../../../NoRecallsModal/NoRecallsModal';
import RecallsLoadingModal from '../../../RecallsLoadingModal/RecallsLoadingModal';
import {
  TKey,
  TMaintenanceDetailsProps,
  TOptionsState,
  TOrderStyles,
  TTabOrderField,
  TTabOrderMap,
} from './types';
import { useModal } from '../../../../../hooks/useModal/useModal';
import { useException } from '../../../../../hooks/useException/useException';
import { isAndroid } from 'react-device-detect';
import { FormWithSelectors } from './FormWithSelectors/FormWithSelectors';
import FormWithAutocompletes from './FormWithAutocompletes/FormWithAutocompletes';
import { blankOptions } from './constants';
import VinCodeInput from './VinCodeInput/VinCodeInput';
import { checkVin } from '../../../../../utils/svAppointments';
import { decodeSCID } from '../../../../../utils/utils';
import { Api } from '../../../../../api/ApiEndpoints/ApiEndpoints';
import { useParams } from 'react-router-dom';
import ManufacturerDidNotReturnRecalls from '../../../ManufacturerDidNotReturnRecalls/ManufacturerDidNotReturnRecalls';
import {
  getRecallsByVin,
  setHasManufacturerDidNotReturnRecalls,
} from '../../../../../store/reducers/recall/actions';
import { ETransportationType } from '../../../../../store/reducers/transportationNeeds/types';
import { ErrorCode } from '../../../../../types/errorCodes';

export const MaintenanceDetailsForm: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<TMaintenanceDetailsProps>>
> = ({ onBack, serviceCategoryPage, handleNext }) => {
  const {
    selectedVehicle,
    makes,
    service,
    valueService,
    subService,
    userType,
    recallsAreShown,
    serviceCategories,
    selectedPackage,
    selectedRecalls,
    serviceTypeOption,
    transportation,
    customer,
  } = useSelector((state: RootState) => state.appointmentFrame);
  const { allCategories } = useSelector(({ categories }: RootState) => categories);
  const { customerLoadedData, scProfile, selectedSR, isEditMode } = useSelector(
    (state: RootState) => state.appointment
  );
  const { id } = useParams<{ id: string }>();
  const { mileage, engineTypes } = useSelector((state: RootState) => state.vehicleDetails);
  const { recallByVinLoading, recallsByVin, hasManufacturerDidNotReturnRecalls } = useSelector(
    (state: RootState) => state.recalls
  );
  const { currentConfig } = useSelector((state: RootState) => state.bookingFlowConfig);
  const [errors, setErrors] = useState<TKey[]>([]);
  const [loadedOptions, setLoadedOptions] = useState<TOptionsState>(blankOptions);
  const [currentModels, setCurrentModels] = useState<string[] | []>([]);
  const [selectedEngine, setSelectedEngine] = useState<IEngineType | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [loadingWhenNextClicked, setLoadingWhenNextClicked] = useState<boolean>(false);

  const dispatch = useDispatch();
  const showError = useException();
  const theme = useTheme();
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useModal();
  const {
    isOpen: isNoRecallsOpen,
    onOpen: onNoRecallsOpen,
    onClose: onNoRecallsClose,
  } = useModal();
  const {
    isOpen: isManufacturerRecallsOpen,
    onOpen: onManufacturerRecallsOpen,
    onClose: onManufacturerRecallsClose,
  } = useModal();

  const isXS = useMediaQuery(theme.breakpoints.down('sm'));
  const isSM = useMediaQuery(theme.breakpoints.down('md'));

  const serviceType = useMemo(() => {
    if (serviceTypeOption) {
      return serviceTypeOption.type;
    }

    return transportation?.type === ETransportationType.PickUpDelivery
      ? EServiceType.PickUpDropOff
      : EServiceType.VisitCenter;
  }, [serviceTypeOption, transportation]);

  const transportationOptionId =
    serviceType === EServiceType.VisitCenter
      ? serviceTypeOption?.transportationOption
        ? serviceTypeOption?.transportationOption?.id
        : !serviceTypeOption?.transportationOption && transportation
          ? transportation?.id
          : undefined
      : serviceType === EServiceType.PickUpDropOff
        ? (serviceTypeOption?.transportationOption?.id ?? undefined)
        : undefined;

  const isBmWService = useMemo(
    () =>
      scProfile?.serviceCenterFlag === EServiceCenterName.BMWSchererville ||
      scProfile?.serviceCenterFlag === EServiceCenterName.DealertrackTest,
    [scProfile]
  );

  const existingSelectedVehicleMatch = useMemo(
    () =>
      customerLoadedData?.vehicles.find(v => {
        return (
          (v.vin &&
            selectedVehicle?.vin &&
            v.vin?.toUpperCase() === selectedVehicle?.vin?.toUpperCase()) ||
          (v.make?.toLowerCase() === selectedVehicle?.make?.toLowerCase() &&
            v.model?.toLowerCase() === selectedVehicle?.model?.toLowerCase() &&
            v.year &&
            v.year?.toString() === selectedVehicle?.year?.toString())
        );
      }),
    [selectedVehicle, customerLoadedData]
  );

  const recallsToggledOn = useMemo(
    () =>
      (currentConfig?.checkRecallsNew && userType === EUserType.New) ||
      (currentConfig?.checkRecallsExisting && userType === EUserType.Existing),
    [currentConfig, userType]
  );

  const isRecallsCategorySelected = useMemo(() => {
    const isServiceRecall =
      service?.type == EServiceCategoryType.OpenRecalls &&
      service.page === EServiceCategoryPage.Page1;
    const isSubServiceRecall =
      subService?.type == EServiceCategoryType.OpenRecalls &&
      subService.page === EServiceCategoryPage.Page2;
    return isServiceRecall || isSubServiceRecall;
  }, [service, subService]);

  const requiredFields = useMemo<TKey[]>(() => {
    const fields: TKey[] = ['model', 'year', 'make', 'mileage'];
    if (isRecallsCategorySelected) fields.push('vin' as TKey);
    return fields;
  }, [isRecallsCategorySelected]);

  const onlyRecallsSelected = useMemo(() => {
    const selectedCategories = allCategories.filter(el =>
      serviceCategories.map(item => item.id).includes(el.id)
    );
    const isGeneralCategorySelected = selectedCategories.find(
      el => el.type === EServiceCategoryType.GeneralCategory
    );
    return (
      !selectedSR.length &&
      !selectedPackage &&
      !isGeneralCategorySelected &&
      isRecallsCategorySelected
    );
  }, [selectedSR, selectedPackage, isRecallsCategorySelected, serviceCategories, allCategories]);

  const isNextDisabled = useMemo(() => {
    return !(
      selectedVehicle?.make &&
      selectedVehicle?.model &&
      selectedVehicle?.year &&
      selectedVehicle?.mileage &&
      (currentConfig?.engineType
        ? selectedVehicle?.engineTypeId
          ? !!engineTypes?.find(engineType => engineType.id === selectedVehicle.engineTypeId)
          : false
        : true) &&
      (isRecallsCategorySelected ? selectedVehicle?.vin : true)
    );
  }, [selectedVehicle, currentConfig, isRecallsCategorySelected]);

  useEffect(() => {
    return () => {
      if (isRecallsCategorySelected && !selectedRecalls.length) {
        removeRecallCategory();
      }
    };
  }, []);

  useEffect(() => {
    const selectedMileage = mileage.find(
      item => item.value.toString() === selectedVehicle?.mileage?.toString()
    );
    const mileageIsValid =
      selectedMileage && selectedMileage?.value.toString() === selectedVehicle?.mileage?.toString();
    if (selectedVehicle) {
      if (selectedVehicle.vin && !checkVin(selectedVehicle.vin)) {
        const vin = selectedVehicle.vin.toUpperCase();
        if (checkVin(vin)) dispatch(updateVehicle({ vin }));
      }
      if (selectedMileage?.value) {
        const theSameMileageSelected = selectedVehicle?.mileage === selectedMileage?.value;
        if (mileageIsValid && !theSameMileageSelected) {
          dispatch(updateVehicle({ mileage: selectedMileage.value }));
        }
      } else if (selectedVehicle.mileage) {
        dispatch(updateVehicle({ mileage: null }));
      }
    }
  }, [dispatch, mileage, selectedVehicle]);

  useEffect(() => {
    if (selectedVehicle?.engineTypeId && engineTypes.length) {
      const option = engineTypes.find(item => item.id === Number(selectedVehicle.engineTypeId));
      if (option) setSelectedEngine(option);
    }
  }, [selectedVehicle, engineTypes]);

  useEffect(() => {
    dispatch(setVehicleDataFromValueService());
  }, [valueService, makes, isBmWService]);

  useEffect(() => {
    if (currentModels.length) {
      setLoadedOptions(prevOptions => ({ ...prevOptions, model: currentModels }));
    }
  }, [currentModels]);

  useEffect(() => {
    if (makes.length) {
      setLoadedOptions(prevOptions => ({ ...prevOptions, make: makes.map(item => item.name) }));
    } else {
      setLoadedOptions(prevOptions => ({ ...prevOptions, make: [t('Other')] }));
    }
    if (selectedVehicle?.make) {
      const currentMake = makes.find(
        item => item.name.toLowerCase() === selectedVehicle.make.toLowerCase()
      );
      if (currentMake)
        setLoadedOptions(prevOptions => ({
          ...prevOptions,
          model: currentMake.models.map(model => model.name),
        }));
    } else {
      setCurrentModels(() =>
        makes
          .map(item => item.models)
          .flat()
          .map(item => item.name)
      );
    }
  }, [makes, selectedVehicle]);

  useEffect(() => {
    if (!selectedVehicle?.make) {
      const defaultMake = makes.find(item => item.id === scProfile?.defaultVehicleMakeId);
      if (defaultMake) {
        if (selectedVehicle) dispatch(setVehicle({ ...selectedVehicle, make: defaultMake.name }));
        setCurrentModels(defaultMake.models.map(model => model.name));
      }
    }
  }, [makes, selectedVehicle]);

  const removeRecallCategory = () => {
    if (
      service?.type === EServiceCategoryType.OpenRecalls &&
      serviceCategoryPage === EServiceCategoryPage.Page1
    ) {
      dispatch(selectService(null));
      dispatch(selectCategories(serviceCategories.filter(el => el.id !== service.id)));
    }
    if (
      subService?.type === EServiceCategoryType.OpenRecalls &&
      serviceCategoryPage === EServiceCategoryPage.Page2
    ) {
      dispatch(selectSubService(null));
      dispatch(selectCategories(serviceCategories.filter(el => el.id !== subService.id)));
    }
  };

  const handleBack = () => {
    onBack(
      service?.type === EServiceCategoryType.Diagnose ||
        subService?.type === EServiceCategoryType.IndividualServices
        ? 'opsCode'
        : 'serviceNeeds'
    );
  };

  const onNext = () => {
    if (isValid()) handleNext();
  };

  const handleDeclineRecalls = () => {
    dispatch(setSelectedRecalls([]));
    if (isRecallsCategorySelected) {
      if (onlyRecallsSelected) {
        dispatch(clearAppointmentSteps('serviceNeeds'));
        onBack('serviceNeeds');
      } else {
        onNext();
      }
    } else {
      onNext();
    }
  };

  const handleAddServices = () => {
    onBack('serviceNeeds');
  };

  const checkVINforRecallCategory = () => {
    if (!selectedVehicle?.vin || !checkVin(selectedVehicle?.vin)) {
      setErrors(prev => [...prev, 'vin']);
      showError(t('VIN is not correct'));
    } else {
      onNoRecallsOpen();
    }
  };

  const handleNoRecalls = () => {
    if (serviceCategories.length < 2 && isRecallsCategorySelected) {
      checkVINforRecallCategory();
    } else {
      onNext();
    }
  };

  useEffect(() => {
    if (!loadingWhenNextClicked || recallByVinLoading) return;

    setLoadingWhenNextClicked(false);
    setLoading(false);
    dispatch(setRecallsAreShown(true));
    if (hasManufacturerDidNotReturnRecalls) {
      dispatch(setHasManufacturerDidNotReturnRecalls(false));
      onManufacturerRecallsOpen();
      return;
    }
    if (recallsByVin.length) {
      onOpen();
    } else {
      onNoRecallsOpen();
    }
  }, [
    loadingWhenNextClicked,
    recallByVinLoading,
    recallsByVin,
    hasManufacturerDidNotReturnRecalls,
  ]);

  const isValid = () => {
    const errorsArray: string[] = [];
    for (const f of requiredFields) {
      if (!selectedVehicle || !selectedVehicle[f as keyof ILoadedVehicle]) {
        setErrors(e => [...e, f]);
        if (f === 'mileage' && !selectedVehicle?.mileage) {
          errorsArray.push(t('Estimated Mileage'));
        } else {
          errorsArray.push(f);
        }
      }
    }
    if (currentConfig?.engineType && !selectedEngine) {
      errorsArray.push(scProfile?.engineTypeFieldName ?? 'Engine Type');
      setErrors(e => [...e, 'engineTypeId']);
    }

    if (errorsArray.length) {
      const fields = errorsArray.map(error => error[0].toUpperCase() + error.slice(1));
      const message = fields
        .join(', ')
        .concat(fields.length < 2 ? ` ${t('is')}` : ` ${t('are')}`)
        .concat(` ${t('required')}`);
      showError(message);
    }
    if (selectedVehicle?.vin && !checkVin(selectedVehicle.vin)) {
      setErrors(prev => [...prev, 'vin']);
      showError(t('VIN is not correct'));
      return false;
    }

    return !errorsArray.length;
  };

  const handleSubmit = async () => {
    if (isRecallsCategorySelected && !recallsToggledOn) {
      showError(
        t('Recalls are unavailable due to restricted access. Please contact your manager.')
      );
      return;
    }

    const recallsFromTheAdmin = !recallsAreShown && recallsToggledOn;
    const makeInTheList = makes.find(
      item => item.name.toLowerCase() === selectedVehicle?.make.toLowerCase()
    );
    if (
      selectedVehicle &&
      makeInTheList &&
      (!customerLoadedData?.isUpdating || isRecallsCategorySelected)
    ) {
      const { vin, make, year, model } = selectedVehicle;
      if (checkVin(vin)) {
        if (vin && make && (recallsFromTheAdmin || isRecallsCategorySelected) && year) {
          const vinFromExistingCustomer = customerLoadedData?.vehicles?.find(
            v => v.vin === vin
          )?.vin;
          if (
            (customerLoadedData?.fromSearchByName || userType === EUserType.Existing) &&
            vinFromExistingCustomer?.length &&
            !isEditMode
          ) {
            if (recallByVinLoading) {
              setLoading(true);
              setLoadingWhenNextClicked(true);
              return;
            }

            dispatch(setRecallsAreShown(true));

            if (hasManufacturerDidNotReturnRecalls) {
              dispatch(setHasManufacturerDidNotReturnRecalls(false));
              onManufacturerRecallsOpen();
              return;
            }

            if (recallsByVin.length) {
              onOpen();
            } else {
              if (isRecallsCategorySelected) {
                onNoRecallsOpen();
              } else {
                onNext();
              }
            }
          } else {
            setLoading(true);
            try {
              const customerId = customerLoadedData?.id ? +customerLoadedData?.id : customer?.id;
              const serviceTypeOptionId = serviceTypeOption?.id;
              const { data: receivedRecalls } = await Api.call(Api.endpoints.Recalls.GetByVin, {
                data: {
                  serviceCenterId: decodeSCID(id),
                  vin: vin.toUpperCase(),
                  make,
                  year: +year,
                  model,
                  serviceTypeOptionId,
                  transportationOptionId,
                  customerId,
                },
              });
              dispatch(setRecallsAreShown(true));
              if (receivedRecalls.length) {
                dispatch(getRecallsByVin(receivedRecalls));
                onOpen();
              } else {
                onNoRecallsOpen();
              }
              setLoading(false);
            } catch (err) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const errors = (err as any).response?.data?.errors;
              if (errors?.length) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                errors.forEach((err: { message: string }) => {
                  showError(err.message || '');
                });
              }
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              if ((err as any).response?.data?.errorCode === ErrorCode.TimeoutError) {
                onManufacturerRecallsOpen();
              }
              setLoading(false);
              return;
            }
          }
        } else {
          handleNoRecalls();
        }
      } else {
        if (!vin?.length && !isRecallsCategorySelected && !requiredFields.includes('vin')) {
          handleNoRecalls();
        } else {
          setErrors(prev => [...prev, 'vin']);
          showError(t('VIN is not correct'));
        }
      }
    } else {
      handleNoRecalls();
    }
    if (!recallByVinLoading) setLoading(false);
  };

  const orderMapStyles = useMemo<TOrderStyles>(
    () => ({
      year: { order: isSM ? 2 : !currentConfig?.engineType && !recallsToggledOn ? 1 : 4 },
      mileage: { order: (currentConfig?.engineType || recallsToggledOn) && !isSM ? 1 : 3 },
      make: { order: 0 },
      model: { order: isSM ? 1 : 2 },
      vin: { order: isSM ? 5 : currentConfig?.engineType ? 5 : 3 },
      engineType: { order: isSM ? 4 : 3 },
    }),
    [isSM, currentConfig?.engineType, recallsToggledOn]
  );

  const tabOrderMap = useMemo<TTabOrderMap>(() => {
    const fields: TTabOrderField[] = ['make', 'year', 'model', 'mileage', 'engineType', 'vin'];
    const orderedFields = [...fields].sort((a, b) => {
      const orderDelta = orderMapStyles[a].order - orderMapStyles[b].order;
      if (orderDelta !== 0) return orderDelta;
      return fields.indexOf(a) - fields.indexOf(b);
    });

    const controlsTabOrder = orderedFields.reduce(
      (acc, field, index) => {
        acc[field] = index + 1;
        return acc;
      },
      {} as Record<(typeof fields)[number], number>
    );

    return {
      ...controlsTabOrder,
      backButton: orderedFields.length + 1,
      nextButton: orderedFields.length + 2,
    };
  }, [orderMapStyles]);

  const onNextForRecalls = () => {
    if (onlyRecallsSelected && !selectedRecalls.length) {
      dispatch(clearAppointmentSteps('serviceNeeds'));
      onBack('serviceNeeds');
    } else {
      onNext();
    }
  };

  return (
    <StepWrapper>
      <Grid container spacing={2}>
        {isAndroid ? (
          <FormWithSelectors
            loadedOptions={loadedOptions}
            setLoadedOptions={setLoadedOptions}
            tabOrderMap={tabOrderMap}
            errors={errors}
            setErrors={setErrors}
            selectedEngine={selectedEngine}
            setSelectedEngine={setSelectedEngine}
            isExistingVehicle={Boolean(existingSelectedVehicleMatch)}
            orderMapStyles={orderMapStyles}
            requiredFields={requiredFields}
            isExistingSelectedVehicle={existingSelectedVehicleMatch}
          />
        ) : (
          <FormWithAutocompletes
            loadedOptions={loadedOptions}
            setLoadedOptions={setLoadedOptions}
            tabOrderMap={tabOrderMap}
            errors={errors}
            setErrors={setErrors}
            selectedEngine={selectedEngine}
            setSelectedEngine={setSelectedEngine}
            isExistingVehicle={Boolean(existingSelectedVehicleMatch)}
            orderMapStyles={orderMapStyles}
            requiredFields={requiredFields}
            isExistingSelectedVehicle={existingSelectedVehicleMatch}
          />
        )}

        <VinCodeInput
          recallsToggledOn={!!recallsToggledOn}
          setErrors={setErrors}
          errors={errors}
          orderMapStyles={orderMapStyles}
          tabOrderMap={tabOrderMap}
          requiredFields={requiredFields}
          isRecallsCategorySelected={isRecallsCategorySelected}
        />
      </Grid>
      <ActionButtons
        onBack={handleBack}
        onNext={handleSubmit}
        backTabIndex={tabOrderMap.backButton}
        nextTabIndex={tabOrderMap.nextButton}
        prevDisabled={isLoading}
        nextDisabled={isNextDisabled || isLoading}
        nextLabel={
          isRecallsCategorySelected
            ? isXS
              ? t('Check Recalls')
              : t('Check for Recalls')
            : t('Next')
        }
      />
      <RecallsByVinModal
        open={isOpen}
        onClose={onClose}
        handleNext={onNextForRecalls}
        handleAddServices={handleAddServices}
        onDeclineRecalls={handleDeclineRecalls}
        isRecallsCategorySelected={isRecallsCategorySelected}
      />
      <NoRecallsModal
        open={isNoRecallsOpen}
        onClose={onNoRecallsClose}
        handleNext={handleDeclineRecalls}
      />
      <ManufacturerDidNotReturnRecalls
        open={isManufacturerRecallsOpen}
        onClose={onManufacturerRecallsClose}
        handleNext={handleDeclineRecalls}
      />
      <RecallsLoadingModal open={isLoading} />
    </StepWrapper>
  );
};
